import mediaQuery from '../../../helpers/mediaQuery';
import isTouchSupported from '../../../helpers/isTouchSupported';
import { isIOS } from '../../../helpers/browserHelpers';
import { lockBodyScroll, unlockBodyScroll } from '../../../helpers/bodyScrollLock';
import { INPUT_ELEMENT_ID } from './search/components/input/input';

function initNavigationEvents(): void {
  const headerElement = document.querySelector<HTMLElement>('.page-header');
  const headerPromoBarElement = document.querySelector<HTMLElement>('.page-header-promo-bar');
  const searchElement = document.querySelector<HTMLElement>('.page-header__search');
  const menuElement = document.querySelector<HTMLElement>('.nav');
  const menuOverlayElement = document.querySelector<HTMLDivElement>('.page-header-backdrop-overlay');
  const menuWrapperElement = document.querySelector<HTMLDivElement>('.nav__wrapper');
  const menuTogglerElement = document.querySelector<HTMLButtonElement>('.page-header__menu-toggler');
  const mainMenuElement = document.querySelector<HTMLDivElement>('.main-menu');
  const secondMenuElement = document.querySelector<HTMLDivElement>('.second-menu');
  const breadCrumbSection = document.querySelector<HTMLElement>('.next-prev-component');

  const freezeBackgroundScrolling = (
    (): ((toFreeze: boolean) => void) =>
    (toFreeze: boolean): void => {
      if (toFreeze) {
        lockBodyScroll();
      } else {
        unlockBodyScroll();
      }
    }
  )();

  function handleClickOutside(e: Event) {
    const isOutside = !(e.target as Element).closest('[data-target-menu]');
    if (isOutside) {
      menuTogglerHandler();
    }
  }

  function menuTogglerHandler(): void {
    const isOpened = menuTogglerElement?.getAttribute('aria-pressed') === 'true';
    menuTogglerElement?.setAttribute('aria-pressed', String(!isOpened));
    menuTogglerElement?.classList[isOpened ? 'remove' : 'add']('page-header__menu-toggler--opened');
    menuElement?.classList[isOpened ? 'add' : 'remove']('hide-up-to-lg');
    menuOverlayElement?.classList[isOpened ? 'add' : 'remove']('hide');
    setMenuDistanceToTheTopOfThePage(mediaQuery.isXLargeUp());
    freezeBackgroundScrolling(!isOpened);
    closeOpenedMenuItems();
    if (!isOpened) {
      document.addEventListener('click', handleClickOutside, { capture: true });
    } else {
      document.removeEventListener('click', handleClickOutside, { capture: true });
    }
    function closeOpenedMenuItems(): void {
      const openedMenuItemElements = document.querySelectorAll(
        '.main-menu__item--has-children, .second-menu__item--has-children'
      );
      [...openedMenuItemElements].forEach((element) => element.classList.remove('_is-open'));
    }
  }

  function menuClickHandler(e: MouseEvent): void {
    e.stopPropagation();
    const clickedMenuItem = getClickedMenuItem(e.target as Element | null);
    if (clickedMenuItem) {
      e.preventDefault();
      menuItemToggler(clickedMenuItem as HTMLElement);
    }

    function menuItemToggler(menuItem: HTMLElement): void {
      menuItem.classList[menuItem.classList.contains('_is-open') ? 'remove' : 'add']('_is-open');
      if (menuItem.classList.contains('main-menu__item--has-children')) {
        const clickedSecondMenuSiblings = menuItem.parentNode ? menuItem.parentNode.children : [];
        [...clickedSecondMenuSiblings].forEach(
          (sibling) =>
            sibling !== clickedMenuItem &&
            sibling.classList.contains('main-menu__item--has-children') &&
            sibling.classList.remove('_is-open')
        );
      }
      if (menuItem.classList.contains('second-menu__item--has-children')) {
        const clickedSecondMenuSiblings = menuItem.parentNode ? menuItem.parentNode.children : [];
        [...clickedSecondMenuSiblings].forEach(
          (sibling) =>
            sibling !== clickedMenuItem &&
            sibling.classList.contains('second-menu__item--has-children') &&
            sibling.classList.remove('_is-open')
        );
      }
    }

    function getClickedMenuItem(clickedElement: Element | null): Element | null | undefined {
      const elementWithClassName = (className) =>
        clickedElement?.classList.contains(className) ||
        (clickedElement?.parentNode as HTMLElement)?.classList.contains(className);
      const mainMenuItem = ['main-menu__link', mediaQuery.isSmallOnly() ? 'second-menu__back' : '']
        .filter(Boolean)
        .find(elementWithClassName)
        ? clickedElement?.closest('.main-menu__item--has-children')
        : null;
      const secondMenuItem = ['second-menu__link'].find(elementWithClassName)
        ? clickedElement?.closest('.second-menu__item--has-children')
        : null;
      return secondMenuItem || mainMenuItem;
    }
  }

  function stickyHeaderHandler(): void {
    const financeModal = document.querySelector('.finance-modal');
    if (document.documentElement.classList.contains('page-header-prevent-body-scroll')) {
      return;
    }

    if (document.documentElement.classList.contains('ios-page-header-prevent-scroll-on-html')) {
      return;
    }

    const promoBarHeight = headerPromoBarElement?.offsetHeight || 0;
    const headerHeight = headerElement?.offsetHeight || 0;
    const searchHeight = mediaQuery.isSmallOnly() ? searchElement?.offsetHeight || 0 : 0;

    if (window.pageYOffset >= promoBarHeight && !mediaQuery.isXLargeUp()) {
      headerElement?.classList.add('page-header--fixed');
      breadCrumbSection?.classList.add('next-prev-component--header-fixed');
      if (!financeModal) {
        document.documentElement.style.setProperty('margin-top', `${headerHeight + searchHeight}px`);
      }
    } else {
      headerElement?.classList.remove('page-header--fixed');
      breadCrumbSection?.classList.remove('next-prev-component--header-fixed');
      if (!financeModal) {
        document.documentElement.style.removeProperty('margin-top');
      }
    }
  }

  function removeRubberEffect(e: Event) {
    if (e.type === 'touchmove') {
      e.stopPropagation();
    }
    if (e.type === 'touchstart') {
      const element = e.currentTarget as HTMLElement;
      const top = element.scrollTop;
      const totalScroll = element.scrollHeight;
      const currentScroll = top + element.offsetHeight;

      if (top === 0) {
        element.scrollTop = 1;
      }

      if (currentScroll === totalScroll) {
        element.scrollTop = top - 1;
      }
    }
  }

  function changeThirdMenuBrandPosition(): void {
    // TODO: Talk to Beaverbrooks and remove this nasty hardcoded piece
    // Third menu is hidden on Mobile/Tablet, but displayed on Desktop to span "By Brand" and "Brands A-Z" categories to more than 2 columns.
    // Watches > "By Brand" menu  is displayed in 2 columns in desktop
    // Brands > "Brands A-Z"  is displayed in 3 columns in desktop
    // Usually the last column would appear with an empty title which makes a feel like it's a continuation of the previous column.
    // This is a legacy hack to avoid blank accordion second-menu title on mobile, instead gluing it with "By Brand" menu.
    const splittedOnDesktopBrandsList = ['by brand', 'brands a-z'];

    [...document.querySelectorAll('.main-menu__item--has-children')].forEach((menuItem) => {
      [...menuItem.querySelectorAll('.second-menu__item--has-children')].forEach((secondMenu) => {
        const hasEmptyTitle = secondMenu.querySelector('.second-menu__link--title-empty');
        if (hasEmptyTitle) {
          const thirdMenuList = secondMenu.querySelector('.third-menu')?.cloneNode(true) as HTMLDivElement;
          const brandCategory = [...menuItem.querySelectorAll('.second-menu__link--title')].find(
            (title) => title.textContent && splittedOnDesktopBrandsList.includes(title.textContent.trim().toLowerCase())
          );
          if (brandCategory && thirdMenuList) {
            thirdMenuList.classList.add('hide-lg-up');
            brandCategory.parentNode?.appendChild(thirdMenuList);
          }
        }
      });
    });
  }

  interface MenuData {
    element: HTMLDivElement;
    enterDelay: number | null | undefined;
    leaveDelay: number | null | undefined;
  }

  function menuHoverHandler(menuData: MenuData): (e: MouseEvent) => void {
    return (e: MouseEvent): void => {
      if (e.type === 'mouseover') {
        if (menuData.leaveDelay) {
          clearTimeout(menuData.leaveDelay);
        }
        menuData.enterDelay = window.setTimeout(() => {
          menuData.element.classList.add('_is-hover');
          menuOverlayElement?.classList.remove('hide');
        }, 300);
      }
      if (e.type === 'mouseout') {
        if (menuData.enterDelay) {
          window.clearTimeout(menuData.enterDelay);
        }
        menuData.leaveDelay = window.setTimeout(() => {
          menuData.element.classList.remove('_is-hover');
          menuOverlayElement?.classList.add('hide');
        }, 50);
      }
    };
  }

  function menuTouchHandler(menuData: MenuData, allMenuElements: NodeListOf<HTMLDivElement>): (e: TouchEvent) => void {
    return (e: TouchEvent): void => {
      if (e.touches.length !== 1) {
        return;
      }

      e.stopPropagation();

      if (!menuData.element.classList.contains('_is-hover') && mediaQuery.isXLargeUp()) {
        e.preventDefault();

        [...allMenuElements].forEach((element) => element.classList.remove('_is-hover'));
        menuData.element.classList.add('_is-hover');
        menuOverlayElement?.classList.remove('hide');
        document.addEventListener(
          'touchstart',
          function hideOnOutsideTouch(event) {
            const isClickOnSearchInput = (event.target as Element).id === INPUT_ELEMENT_ID;
            if (!isClickOnSearchInput) {
              event.stopPropagation();
              event.preventDefault();
            }
            menuOverlayElement?.classList.add('hide');
            [...allMenuElements].forEach((element) => element.classList.remove('_is-hover'));
            document.removeEventListener('touchstart', hideOnOutsideTouch);
          },
          { passive: false }
        );
      }
    };
  }

  function setMenuDistanceToTheTopOfThePage(isXLargeUp: boolean): void {
    if (isXLargeUp) {
      menuElement?.style.removeProperty('height');
      menuWrapperElement?.style.removeProperty('height');
    } else {
      // Resetting height before calculations
      menuElement?.style.setProperty('height', '0px');
      menuWrapperElement?.style.setProperty('height', '0px');

      const promoBarHeight = headerPromoBarElement?.offsetHeight || 0;
      const headerHeight = headerElement?.offsetHeight || 0;

      const isHeaderFixed = headerElement?.classList.contains('page-header--fixed');
      const scrollYOverPromo = isHeaderFixed
        ? promoBarHeight
        : Math.abs(headerPromoBarElement?.getBoundingClientRect().top || 0);
      const distanceToTheTop = headerHeight + promoBarHeight - scrollYOverPromo;

      menuElement?.style.setProperty('height', `calc(${window.innerHeight}px - ${distanceToTheTop}px)`);
      menuWrapperElement?.style.setProperty('height', `calc(${window.innerHeight}px - ${distanceToTheTop}px)`);
    }
  }

  function menuOverlayCloseDesktopHandler(): void {
    menuOverlayElement?.classList.add('hide');
    const allMenuItemElements = document.querySelectorAll<HTMLDivElement>('.main-menu__item--has-children');
    [...allMenuItemElements].forEach((element) => element.classList.remove('_is-hover'));
  }

  const initMobileTabletNavigationEvents = (
    () =>
    (isXLargeUp: boolean): void => {
      if (isXLargeUp) {
        menuTogglerElement?.removeEventListener('click', menuTogglerHandler);
        mainMenuElement?.removeEventListener('click', menuClickHandler);
        if (isIOS()) {
          secondMenuElement?.removeEventListener('touchstart', removeRubberEffect);
          secondMenuElement?.removeEventListener('touchmove', removeRubberEffect);
        }
      } else {
        menuTogglerElement?.addEventListener('click', menuTogglerHandler);
        mainMenuElement?.addEventListener('click', menuClickHandler);
        window.addEventListener('scroll', stickyHeaderHandler);
        stickyHeaderHandler();
        if (isIOS()) {
          secondMenuElement?.addEventListener('touchstart', removeRubberEffect);
          secondMenuElement?.addEventListener('touchmove', removeRubberEffect);
        }
      }
    }
  )();

  const initDesktopNavigationEvents = (() => {
    const allMenuItemElements = document.querySelectorAll<HTMLDivElement>('.main-menu__item--has-children');
    const allMenuItemElementsWithData = [...allMenuItemElements].map((element) => ({
      element,
      mouseHandler: menuHoverHandler({ element, leaveDelay: null, enterDelay: null }),
      touchHandler: menuTouchHandler({ element, leaveDelay: null, enterDelay: null }, allMenuItemElements)
    }));

    return (isXLargeUp: boolean): void => {
      if (isXLargeUp) {
        allMenuItemElementsWithData.forEach((menuData) => {
          menuData.element.addEventListener('mouseover', menuData.mouseHandler);
          menuData.element.addEventListener('mouseout', menuData.mouseHandler);
          if (isTouchSupported()) {
            menuData.element.addEventListener('touchstart', menuData.touchHandler);
          }
        });
      } else {
        allMenuItemElementsWithData.forEach((menuData) => {
          menuData.element.removeEventListener('mouseover', menuData.mouseHandler);
          menuData.element.removeEventListener('mouseout', menuData.mouseHandler);
          if (isTouchSupported()) {
            menuData.element.removeEventListener('touchstart', menuData.touchHandler);
          }
        });
      }
    };
  })();

  function cleanUpOnViewportResize(isXLargeUp: boolean): void {
    const isMobileTabletMenuOpened = menuTogglerElement?.getAttribute('aria-pressed') === 'true';
    if (isXLargeUp && isMobileTabletMenuOpened) {
      menuTogglerHandler();
    } else {
      menuOverlayCloseDesktopHandler();
    }
  }

  let currentIsXLargeUp = mediaQuery.isXLargeUp();

  function initNavigation() {
    changeThirdMenuBrandPosition();

    setMenuDistanceToTheTopOfThePage(currentIsXLargeUp);
    initMobileTabletNavigationEvents(currentIsXLargeUp);
    initDesktopNavigationEvents(currentIsXLargeUp);

    window.addEventListener('resize', () => {
      const newIsXLargeUp = mediaQuery.isXLargeUp();
      setMenuDistanceToTheTopOfThePage(newIsXLargeUp);
      if (currentIsXLargeUp !== newIsXLargeUp) {
        currentIsXLargeUp = newIsXLargeUp;
        initMobileTabletNavigationEvents(currentIsXLargeUp);
        initDesktopNavigationEvents(currentIsXLargeUp);
        cleanUpOnViewportResize(currentIsXLargeUp);
      }
    });
  }

  initNavigation();
}

export default initNavigationEvents;
